import React, {
  createContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  lazy,
  Suspense,
  useMemo,
} from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { translations } from "../translations/translations";
import type { Expense } from "../src/types/types";
import axios from "axios";
import { ToastProvider } from "./components/ToastProvider/ToastProvider";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import ReactGA from "react-ga4";
import * as Sentry from "@sentry/react";
import AdminLogin from "./components/Admin/AdminLogin";
import AdminDashboard from "./components/Admin/AdminDashboard";
import SessionWarningModal from "./components/SessionWarning/SessionWarningModal";
import { sessionManager } from "../utils/sessionManager";
import EmailVerified from "./components/Mailer/email-verified";
import AcceptInvite from "./components/AcceptInvite";
import ExpensesList from "./components/ExpensesList";
import CookieConsent from "./components/CookieConsent";

const PageLoader = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
      background: "var(--body-bg)",
    }}
  >
    <div className="w-[60px] h-[60px] border-2 border-white/20 border-t-primary rounded-full animate-spin" />
  </div>
);

const Landing = lazy(() => import("./components/Landing"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const ReportsPage = lazy(() => import("./pages/Reports/ReportsPage"));
const Planner = lazy(() => import("./components/Planner/Planner"));
const PlannerDetail = lazy(() => import("./components/Planner/PlannerDetail"));
const Reimbursements = lazy(() => import("./components/Reimbursements/Reimbursements"));
const Notes = lazy(() => import("./components/Notes/Notes"));
const EmailCapture = lazy(() => import("./pages/EmailCapture"));
const Auth = lazy(() => import("./components/Auth"));
const Dashboard = lazy(() => import("./components/Dashboard"));
const Settings = lazy(() => import("./components/Settings"));
const Subscription = lazy(() => import("./components/Subscription"));
const WaveyAI = lazy(() => import("./components/WaveyAI/WaveyAI"));
const HelpCenter = lazy(() => import("./components/HelpCenter"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./components/TermsOfService"));
const UserGuide = lazy(() => import("./components/UserGuide"));
const Blog = lazy(() => import("./components/Blog"));
const BlogPost = lazy(() => import("./components/BlogPost"));
const CookiePolicy = lazy(() => import("./components/CookiePolicy"));
const ChartPreview = lazy(() => import("./components/ChartPreview"));
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000";

interface AppContextType {
  expenses: Expense[];
  setExpenses: React.Dispatch<React.SetStateAction<Expense[]>>;
  newExpense: {
    name: string;
    price: number;
    dueDate: string;
    wave?: number;
    category: "Wave" | "Paycheck" | "Other";
    paymentUrl: string;
    paymentType: "url" | "app" | "autopay";
    frequency: "weekly" | "biweekly" | "monthly";
    incomeSourceId: string;
  };
  setNewExpense: React.Dispatch<React.SetStateAction<any>>;
  editingId: string | null;
  setEditingId: React.Dispatch<React.SetStateAction<string | null>>;
  token: string | null;
  setToken: React.Dispatch<React.SetStateAction<string | null>>;
  message: string;
  setMessage: React.Dispatch<React.SetStateAction<string>>;
  theme: "light" | "dark";
  setTheme: React.Dispatch<React.SetStateAction<"light" | "dark">>;
  isFormExpanded: boolean;
  setIsFormExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  openMenuId: string | null;
  setOpenMenuId: React.Dispatch<React.SetStateAction<string | null>>;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  waveFilter:
    | "All"
    | "Week 1"
    | "Week 2"
    | "Week 3"
    | "Week 4"
    | "Wave 1"
    | "Wave 2"
    | "Credit Card";
  setWaveFilter: React.Dispatch<
    React.SetStateAction<
      | "All"
      | "Week 1"
      | "Week 2"
      | "Week 3"
      | "Week 4"
      | "Wave 1"
      | "Wave 2"
      | "Credit Card"
    >
  >;
  expenseListFrequency: "weekly" | "biweekly" | "monthly";
  setExpenseListFrequency: React.Dispatch<
    React.SetStateAction<"weekly" | "biweekly" | "monthly">
  >;
  // waveAmount: number;
  // setWaveAmount: React.Dispatch<React.SetStateAction<number>>;
  wave1Amount: number;
  setWave1Amount: (val: number) => void;
  wave2Amount: number;
  setWave2Amount: (val: number) => void;
  wave3Amount: number;
  setWave3Amount: (val: number) => void;
  wave4Amount: number;
  setWave4Amount: (val: number) => void;
  language: "en" | "es";
  setLanguage: React.Dispatch<React.SetStateAction<"en" | "es">>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  t: (typeof translations)[keyof typeof translations];
  setMessageWithTimeout: (msg: string) => () => void;
  isSetupComplete: boolean | null;
  setIsSetupComplete: React.Dispatch<React.SetStateAction<boolean | null>>;
  subscriptionTier: "free" | "standard" | "premium";
  setSubscriptionTier: React.Dispatch<
    React.SetStateAction<"free" | "standard" | "premium">
  >;
  themeColor: "blue" | "green" | "purple" | "custom";
  setThemeColor: React.Dispatch<
    React.SetStateAction<"blue" | "green" | "purple" | "custom">
  >;
  applyThemeColors: (
    color: "blue" | "green" | "purple" | "custom",
    customColors?: { primary: string; primaryDark: string }
  ) => void;
  customThemeColors: { primary: string; primaryDark: string } | null;
  setCustomThemeColors: React.Dispatch<
    React.SetStateAction<{ primary: string; primaryDark: string } | null>
  >;
  fetchExpenses: () => Promise<void>;
  fetchIncomes: () => Promise<void>;
  fetchSettings: () => Promise<void>;
  incomeSources: Array<{
    id: string;
    name: string;
    amount1?: number;
    amount2?: number;
    amount3?: number;
    amount4?: number;
    frequency: "weekly" | "biweekly" | "monthly" | "one-time";
    color: string;
  }>;
  setIncomeSources: React.Dispatch<React.SetStateAction<any>>;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalExpenses: number;
    limit: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  } | null;
  setPagination: React.Dispatch<React.SetStateAction<any>>;
}

export const AppContext = createContext<AppContextType | undefined>(undefined);

interface ProtectedRouteProps {
  children: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const token = localStorage.getItem("token");
  if (!token) {
    return <Navigate to="/auth" replace />;
  }
  return children;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (axios.isAxiosError(error) && error.response?.status === 429) {
          return false;
        }
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
    mutations: {
      retry: (failureCount, error) => {
        if (axios.isAxiosError(error) && error.response?.status === 429) {
          return false;
        }
        return failureCount < 1;
      },
      retryDelay: 1000,
    },
  },
});

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: { "Cache-Control": "no-cache" },
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;

    // Handle 401 Unauthorized - redirect to auth
    if (response && response.status === 401) {
      // Clear local storage and redirect to auth
      localStorage.removeItem("token");
      localStorage.removeItem("biometricCredentialId");

      // Only redirect if not already on auth page to prevent infinite loops
      if (window.location.pathname !== "/auth") {
        window.location.href = "/auth";
      }
      return Promise.reject(error);
    }

    // Handle 429 Rate Limiting
    if (response && response.status === 429) {
      const retryAfter = response.headers["retry-after"]
        ? parseInt(response.headers["retry-after"]) * 1000
        : 1000;
      const maxRetries = 3;
      if (config.retryCount < maxRetries) {
        config.retryCount = (config.retryCount || 0) + 1;
        const delay = Math.min(
          retryAfter * Math.pow(2, config.retryCount - 1),
          10000
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
        return axiosInstance(config);
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  // All state declarations
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [newExpense, setNewExpense] = useState({
    name: "",
    price: 0,
    dueDate: "",
    wave: 1 as number | undefined,
    category: "Wave" as "Wave" | "Paycheck" | "Other",
    paymentUrl: "",
    paymentType: "url" as "url" | "app" | "autopay",
    frequency: "biweekly" as "weekly" | "biweekly" | "monthly",
    incomeSourceId: "",
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(
    localStorage.getItem("token")
  );
  const [message, setMessage] = useState("");
  const [theme, setTheme] = useState<"light" | "dark">(
    (localStorage.getItem("theme") || "dark") as "light" | "dark"
  );
  const [isFormExpanded, setIsFormExpanded] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [waveFilter, setWaveFilter] = useState<
    | "All"
    | "Week 1"
    | "Week 2"
    | "Week 3"
    | "Week 4"
    | "Wave 1"
    | "Wave 2"
    | "Credit Card"
  >("All");
  const [expenseListFrequency, setExpenseListFrequency] = useState<
    "weekly" | "biweekly" | "monthly"
  >("biweekly");
  const [wave1Amount, setWave1Amount] = useState(0);
  const [wave2Amount, setWave2Amount] = useState(0);
  const [wave3Amount, setWave3Amount] = useState(0);
  const [wave4Amount, setWave4Amount] = useState(0);
  const [language, setLanguageState] = useState<"en" | "es">(() => {
    const saved = localStorage.getItem("language");
    return saved === "es" || saved === "en" ? saved : "en";
  });

  // Wrapper to save to localStorage AND backend when language changes
  const setLanguage = useCallback(
    (lang: "en" | "es" | ((prev: "en" | "es") => "en" | "es")) => {
      setLanguageState((prev) => {
        const newLang = typeof lang === "function" ? lang(prev) : lang;
        localStorage.setItem("language", newLang);

        // Also save to backend to persist across sessions
        const token = localStorage.getItem("token");
        if (token) {
          axios
            .put(
              `${API_URL}/settings`,
              { language: newLang },
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            )
            .catch((err) => {
              console.error("Failed to save language to backend:", err);
            });
        }

        return newLang;
      });
    },
    []
  );

  const [loading, setLoading] = useState(false);
  const [isSetupComplete, setIsSetupComplete] = useState<boolean | null>(null);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState<
    "free" | "standard" | "premium"
  >("free");
  const [themeColor, setThemeColor] = useState<
    "blue" | "green" | "purple" | "custom"
  >("blue");
  const [incomeSources, setIncomeSources] = useState<
    Array<{
      id: string;
      name: string;
      amount1: number;
      amount2: number;
      amount3: number;
      amount4: number;
      frequency: "weekly" | "biweekly" | "monthly" | "one-time";
      color: string;
    }>
  >([]);
  const [pagination, setPagination] = useState<{
    currentPage: number;
    totalPages: number;
    totalExpenses: number;
    limit: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  } | null>(null);
  const [customThemeColors, setCustomThemeColors] = useState<{
    primary: string;
    primaryDark: string;
  } | null>(null);

  const isInitialLoad = useRef(true);
  const hasFetchedSettings = useRef(false);
  const t = translations[language];

  const setMessageWithTimeout = useCallback((msg: string) => {
    setMessage(msg);
    const timer = setTimeout(() => setMessage(""), 3000);
    return () => clearTimeout(timer);
  }, []);

  // ✅ FIXED: Stable applyThemeColors with no dependencies
  const applyThemeColors = useCallback(
    (
      color: "blue" | "green" | "purple" | "custom",
      customColors?: { primary: string; primaryDark: string }
    ) => {
      const root = document.documentElement;
      const themePresets = {
        blue: { primary: "#007AFF", primaryDark: "#0051D5" },
        green: { primary: "#34C759", primaryDark: "#248A3D" },
        purple: { primary: "#AF52DE", primaryDark: "#8E44AD" },
      };

      if (color === "custom" && customColors) {
        root.style.setProperty("--primary-blue", customColors.primary);
        root.style.setProperty("--primary-blue-dark", customColors.primaryDark);
      } else if (color !== "custom" && themePresets[color]) {
        root.style.setProperty("--primary-blue", themePresets[color].primary);
        root.style.setProperty(
          "--primary-blue-dark",
          themePresets[color].primaryDark
        );
      } else {
        root.style.setProperty("--primary-blue", "#007AFF");
        root.style.setProperty("--primary-blue-dark", "#0051D5");
      }
    },
    [] // NO dependencies - function is stable
  );

  // ✅ FIXED: fetchExpenses with stable dependencies
  const fetchExpenses = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(`${API_URL}/expenses`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Cache-Control": "no-cache",
        },
        params: {
          page: 1,
          limit: 10000,
        },
      });

      if (res.data.expenses) {
        setExpenses(res.data.expenses);
        setPagination(res.data.pagination);
      } else {
        setExpenses(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch expenses:", err);
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        localStorage.removeItem("token");
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  }, [token]); // Only depends on token

  const fetchIncomes = useCallback(async () => {
    if (!token) return;
    try {
      await axiosInstance.get(`${API_URL}/incomes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.error("Failed to fetch incomes:", err);
    }
  }, [token]);

  // ✅ FIXED: fetchSettings without circular dependencies
  const fetchSettings = useCallback(async () => {
    if (!token || hasFetchedSettings.current) return;

    setLoading(true);
    try {
      const res = await axiosInstance.get(`${API_URL}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Set all state at once
      setWave1Amount(res.data.wave1Amount ?? 0);
      setWave2Amount(res.data.wave2Amount ?? 0);
      setWave3Amount(res.data.wave3Amount ?? 0);
      setWave4Amount(res.data.wave4Amount ?? 0);
      setExpenseListFrequency(res.data.frequency || "biweekly");
      setLanguage(res.data.language || "en");
      setIsSetupComplete(res.data.isSetupComplete ?? null);
      setSubscriptionTier(res.data.subscriptionTier || "free");

      const savedThemeColor = res.data.themeColor || "blue";
      setThemeColor(savedThemeColor);

      // Handle custom colors
      if (savedThemeColor === "custom" && res.data.customThemeColors) {
        const { primary, primaryDark } = res.data.customThemeColors;
        setCustomThemeColors({ primary, primaryDark });
        // Apply immediately without causing re-render loop
        const root = document.documentElement;
        root.style.setProperty("--primary-blue", primary);
        root.style.setProperty("--primary-blue-dark", primaryDark);
      } else {
        // ✅ FIX: Apply theme color presets (blue, green, purple) on load
        const themePresets = {
          blue: { primary: "#007AFF", primaryDark: "#0051D5" },
          green: { primary: "#34C759", primaryDark: "#248A3D" },
          purple: { primary: "#AF52DE", primaryDark: "#8E44AD" },
        };

        if (themePresets[savedThemeColor as keyof typeof themePresets]) {
          const root = document.documentElement;
          const colors =
            themePresets[savedThemeColor as keyof typeof themePresets];
          root.style.setProperty("--primary-blue", colors.primary);
          root.style.setProperty("--primary-blue-dark", colors.primaryDark);
        }
        setCustomThemeColors(null);
      }

      // Handle income sources
      const sources = (res.data.incomeSources || []).map(
        (src: any, idx: number) => {
          const defaultColors = [
            "#007AFF",
            "#34C759",
            "#AF52DE",
            "#FF9500",
            "#FF3B30",
            "#5856D6",
          ];
          return {
            id: src.id || src._id,
            name: src.name,
            amount1: src.amount1,
            amount2: src.amount2,
            amount3: src.amount3,
            amount4: src.amount4,
            frequency: src.frequency,
            color: src.color || defaultColors[idx % defaultColors.length],
          };
        }
      );
      setIncomeSources(sources);

      hasFetchedSettings.current = true;
    } catch (err) {
      console.error("Failed to fetch settings:", err);
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        localStorage.removeItem("token");
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  }, [token]); // ONLY depends on token

  // GA Page View
  useEffect(() => {
    if (import.meta.env.VITE_GA_MEASUREMENT_ID) {
      ReactGA.send({
        hitType: "pageview",
        page: location.pathname + location.search,
        title: document.title,
      });
    }
  }, [location]);

  // Apply theme
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  // System theme detection
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      if (!localStorage.getItem("theme")) {
        const newTheme = mediaQuery.matches ? "dark" : "light";
        if (theme !== newTheme) {
          setTheme(newTheme);
        }
      }
    };
    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  // Check authentication and redirect - SMART LANDING PAGE
  useEffect(() => {
    const currentPath = location.pathname;
    if (
      token &&
      (currentPath === "/" ||
        currentPath === "/landing" ||
        currentPath === "/auth")
    ) {
      // ✅ IMPROVED: New users go to expenses-list, existing users go to dashboard
      const shouldGoToExpensesList =
        isSetupComplete === false || isSetupComplete === null;
      const targetPath = shouldGoToExpensesList
        ? "/expenses-list"
        : "/dashboard";
      navigate(targetPath, { replace: true });
    }
  }, [token, navigate, location.pathname, isSetupComplete]);

  useEffect(() => {
    if (token && isInitialLoad.current) {
      isInitialLoad.current = false;
      Promise.all([fetchExpenses(), fetchIncomes(), fetchSettings()]);
    }
  }, [token]); // Dependencies removed - functions are stable

  // Reset fetch flags when token changes
  useEffect(() => {
    if (token) {
      isInitialLoad.current = true;
      hasFetchedSettings.current = false;
    }
  }, [token]);

  // Handle token changes from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (storedToken && storedToken !== token) {
      setToken(storedToken);
    } else if (!storedToken && token) {
      setToken(null);
    }
  }, [token]);

  // Initialize session manager
  useEffect(() => {
    const isProtectedRoute =
      location.pathname.startsWith("/dashboard") ||
      location.pathname.startsWith("/settings") ||
      location.pathname.startsWith("/subscription");

    if (token && isProtectedRoute) {
      sessionManager.init(navigate, () => setShowSessionWarning(true));
    }

    return () => {
      sessionManager.cleanup();
    };
  }, [token, navigate, location.pathname]);

  // Close session warning modal when token is cleared
  useEffect(() => {
    if (!token && showSessionWarning) {
      setShowSessionWarning(false);
    }
  }, [token, showSessionWarning]);

  useEffect(() => {
    if (!token) {
      setShowSessionWarning(false);
    }
  }, [token]);

  // ✅ MEMOIZED: Context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      expenses,
      setExpenses,
      newExpense,
      setNewExpense,
      editingId,
      setEditingId,
      token,
      setToken,
      message,
      setMessage,
      theme,
      setTheme,
      isFormExpanded,
      setIsFormExpanded,
      openMenuId,
      setOpenMenuId,
      searchQuery,
      setSearchQuery,
      waveFilter,
      setWaveFilter,
      expenseListFrequency,
      setExpenseListFrequency,
      wave1Amount,
      setWave1Amount,
      wave2Amount,
      setWave2Amount,
      wave3Amount,
      setWave3Amount,
      wave4Amount,
      setWave4Amount,
      language,
      setLanguage,
      loading,
      setLoading,
      t,
      setMessageWithTimeout,
      isSetupComplete,
      setIsSetupComplete,
      subscriptionTier,
      setSubscriptionTier,
      themeColor,
      setThemeColor,
      applyThemeColors,
      fetchExpenses,
      fetchIncomes,
      fetchSettings,
      incomeSources,
      setIncomeSources,
      setPagination,
      pagination,
      customThemeColors,
      setCustomThemeColors,
    }),
    [
      expenses,
      newExpense,
      editingId,
      token,
      message,
      theme,
      isFormExpanded,
      openMenuId,
      searchQuery,
      waveFilter,
      expenseListFrequency,
      wave1Amount,
      wave2Amount,
      wave3Amount,
      wave4Amount,
      language,
      loading,
      t,
      setMessageWithTimeout,
      isSetupComplete,
      subscriptionTier,
      themeColor,
      applyThemeColors,
      fetchExpenses,
      fetchIncomes,
      fetchSettings,
      incomeSources,
      pagination,
      customThemeColors,
    ]
  );

  function AdminLoginWrapper() {
    const navigate = useNavigate();

    const handleLogin = (token: string, adminData: any) => {
      localStorage.setItem("adminToken", token);
      localStorage.setItem("adminData", JSON.stringify(adminData));
      navigate("/admin");
    };

    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div
          style={{
            padding: "2rem",
            textAlign: "center",
            fontFamily: "system-ui",
          }}
        >
          <h1>Oops! Something went wrong</h1>
          <p style={{ color: "#666" }}>
            We've been notified and will fix this soon.
          </p>
          <button
            onClick={resetError}
            style={{
              padding: "12px 24px",
              marginTop: "1rem",
              fontSize: "16px",
              backgroundColor: "#007AFF",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            Try Again
          </button>
          {import.meta.env.DEV && (
            <details
              style={{
                marginTop: "2rem",
                textAlign: "left",
                backgroundColor: "#f5f5f5",
                padding: "1rem",
                borderRadius: "8px",
              }}
            >
              <summary>Error Details (Development Only)</summary>
              <pre
                style={{
                  overflow: "auto",
                  fontSize: "12px",
                  marginTop: "1rem",
                }}
              >
                {(error as Error).toString()}
              </pre>
            </details>
          )}
        </div>
      )}
    >
      <ErrorBoundary>
        <ToastProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <AppContext.Provider value={contextValue}>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/landing" element={<Landing />} />
                  <Route path="/email-capture" element={<EmailCapture />} />
                  <Route path="/free-template" element={<EmailCapture />} />
                  <Route
                    path="/user-guide"
                    element={
                      <UserGuide
                        language={language}
                        theme={theme}
                        onClose={() => navigate("/")}
                      />
                    }
                  />
                  <Route
                    path="/auth"
                    element={<Auth theme={theme} language={language} />}
                  />
                  <Route path="/help-center" element={<HelpCenter />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/cookie-policy" element={<CookiePolicy />} />
                  <Route
                    path="/terms-of-service"
                    element={<TermsOfService />}
                  />
                  {/* <Route path="/verify-email" element={<VerifyEmail />} /> */}
                  <Route path="/email-verified" element={<EmailVerified />} />
                  <Route path="/admin/login" element={<AdminLoginWrapper />} />
                  <Route path="/admin/*" element={<AdminDashboard />} />
                  <Route path="/accept-invite" element={<AcceptInvite />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/chart-preview" element={<ChartPreview />} />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/expenses-list" element={<ExpensesList />} />
                  <Route
                    path="/planner"
                    element={
                      <ProtectedRoute>
                        <Planner />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/planner/:id"
                    element={
                      <ProtectedRoute>
                        <PlannerDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
                  <Route path="/reimbursements" element={<ProtectedRoute><Reimbursements /></ProtectedRoute>} />
                  <Route path="/notes" element={<ProtectedRoute><Notes /></ProtectedRoute>} />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <Settings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/subscription"
                    element={
                      <ProtectedRoute>
                        <Subscription />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/wavey-ai"
                    element={
                      <ProtectedRoute>
                        <WaveyAI />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </AppContext.Provider>
          </QueryClientProvider>
        </ToastProvider>
      </ErrorBoundary>
      {token && (
        <SessionWarningModal
          open={showSessionWarning}
          onContinue={() => {
            setShowSessionWarning(false);
            sessionManager.keepAlive();
          }}
          onLogout={() => {
            setShowSessionWarning(false);
            sessionManager.logout("User logged out");
          }}
          language={language}
        />
      )}
      <CookieConsent />
    </Sentry.ErrorBoundary>
  );
}

export default App;
